/**
 * CM Analytics — GA4 Tracking Module for Camilla and Marc
 *
 * Central analytics module that pushes GA4-formatted events to window.dataLayer
 * for consumption by GTM (GTM-K4RD74X7) → GA4 (G-BG3KFR1LK5).
 *
 * Architecture: Rich Theme JS + Thin GTM
 * - This module handles all pre-checkout tracking in the main window
 * - Checkout events (begin_checkout, purchase, etc.) are handled by a separate
 *   customer events pixel in the Shopify sandbox
 *
 * @see docs/tracking-context.md
 */
(function () {
'use strict';

// Guarded so this file can be loaded twice (theme editor preview / AB tests) without throwing.
var MEASUREMENT_ID = typeof MEASUREMENT_ID !== 'undefined' ? MEASUREMENT_ID : 'G-BG3KFR1LK5';

// ---------------------------------------------------------------------------
// 1. DataLayer Core
// ---------------------------------------------------------------------------

function initDataLayer() {
  window.dataLayer = window.dataLayer || [];
}

function pushEvent(name, params = {}) {
  initDataLayer();
  window.dataLayer.push({
    event: name,
    ...params,
  });
}

function pushEcommerceEvent(name, ecommerceData) {
  initDataLayer();
  // Clear previous ecommerce data to prevent bleed between events
  window.dataLayer.push({ ecommerce: null });
  window.dataLayer.push({
    event: name,
    ecommerce: ecommerceData,
  });
}

// ---------------------------------------------------------------------------
// 2. Product Data Cache
// ---------------------------------------------------------------------------

/** @type {Map<string, object>} variantId → GA4 item data */
const productCache = new Map();

function registerProduct(variantId, itemData) {
  productCache.set(String(variantId), itemData);
}

function resolveProduct(variantId) {
  return productCache.get(String(variantId)) || null;
}

// ---------------------------------------------------------------------------
// 3. Currency + Page Context Helpers
// ---------------------------------------------------------------------------

function getCurrency() {
  // Read from Shopify global or meta tag
  if (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) {
    return window.Shopify.currency.active;
  }
  // Fallback: check meta tag set by theme
  const meta = document.querySelector('meta[property="og:price:currency"]');
  if (meta) return meta.content;
  return 'AUD';
}

function getPageContext() {
  const main = document.querySelector('#MainContent');
  return {
    template: main ? main.dataset.template : '',
  };
}

// ---------------------------------------------------------------------------
// 4. GA4 Item Mapping — Shopify Product JSON
// ---------------------------------------------------------------------------

/**
 * Maps a Shopify product JSON object + selected variant to GA4 item format.
 * @param {object} product - Shopify product JSON (from [data-product-json])
 * @param {object} variant - Selected variant object
 * @param {object} [opts] - Additional options
 * @param {number} [opts.index] - Position in list
 * @param {string} [opts.listId] - Collection handle
 * @param {string} [opts.listName] - Collection title
 * @param {number} [opts.quantity] - Quantity (default 1)
 * @returns {object} GA4 item
 */
function mapShopifyProductToGA4Item(product, variant, opts = {}) {
  const price = variant.price != null ? variant.price / 100 : 0;
  const compareAtPrice = variant.compare_at_price ? variant.compare_at_price / 100 : 0;
  const discount = compareAtPrice > price ? +(compareAtPrice - price).toFixed(2) : 0;
  const onSale = discount > 0;

  const item = {
    item_id: product.handle || '',
    item_name: product.title || '',
    item_brand: product.vendor || 'CAMILLA AND MARC',
    item_category: product.type || '',
    item_variant: variant.title || variant.option1 || '',
    price: +price.toFixed(2),
    quantity: opts.quantity || 1,
    affiliation: 'Shopify',
    discount: discount,
    // Custom dimensions matching old Angular setup
    dimension2: variant.option2 || '', // size
    dimension3: variant.available ? 'In Stock' : 'Out of Stock',
    dimension4: onSale ? 'Sale' : '',
  };

  if (opts.index != null) item.index = opts.index;
  if (opts.listId) item.item_list_id = opts.listId;
  if (opts.listName) item.item_list_name = opts.listName;
  if (opts.coupon) item.coupon = opts.coupon;

  return item;
}

// ---------------------------------------------------------------------------
// 5. GA4 Item Mapping — SearchSpring Results
// ---------------------------------------------------------------------------

/**
 * Maps a SearchSpring result object to GA4 item format.
 * Mirrors the old Angular constructProductFromSearchspring() mapping.
 * @param {object} result - SearchSpring result from controller.store.results
 * @param {number} index - Position in list
 * @param {string} listName - Collection/search name
 * @param {string} [listId] - Collection handle
 * @returns {object} GA4 item
 */
function mapSearchSpringResultToGA4Item(result, index, listName, listId) {
  // SS result fields: name, handle, price, sku, brand, variant_id (array),
  // ss_on_sale, ss_inventory_count, images, product_type_unigram, collection_id
  const mappings = result.mappings || result.attributes || result;
  const core = mappings.core || mappings;

  const name = core.name || result.name || '';
  const handle = core.handle || core.url?.split('/products/')?.pop()?.split('?')[0] || result.handle || '';
  const brand = core.brand || result.brand || 'CAMILLA AND MARC';
  const price = parseFloat(core.price || result.price || 0);
  const msrp = parseFloat(core.msrp || result.msrp || 0);
  const discount = msrp > price ? +(msrp - price).toFixed(2) : 0;
  const onSale = result.attributes?.ss_on_sale === 'true' || core.ss_on_sale === 'true' || discount > 0;
  const productType = core.product_type_unigram || result.attributes?.product_type_unigram || result.product_type_unigram || '';
  const inStock = (result.attributes?.ss_inventory_count || core.ss_inventory_count || 0) > 0;

  const item = {
    item_id: handle,
    item_name: name,
    item_brand: brand,
    item_category: productType,
    price: +price.toFixed(2),
    quantity: 1,
    index: index,
    affiliation: 'Shopify',
    discount: discount,
    dimension3: inStock ? 'In Stock' : 'Out of Stock',
    dimension4: onSale ? 'Sale' : '',
  };

  if (listName) item.item_list_name = listName;
  if (listId) item.item_list_id = listId;

  return item;
}

// ---------------------------------------------------------------------------
// 6. Cache Population from Various Sources
// ---------------------------------------------------------------------------

/**
 * Populate cache from Shopify product JSON found on page.
 * Looks for:
 * 1. Elements with [data-product-json] attribute (standard)
 * 2. Wishlist script tags with id="ProductJson-{id}" (rendered by wishlist-pdpbutton.liquid)
 */
function registerFromShopifyJSON() {
  const scripts = document.querySelectorAll('[data-product-json], script[id^="ProductJson-"]');
  scripts.forEach((script) => {
    try {
      const product = JSON.parse(script.textContent);
      if (product && product.variants) {
        product.variants.forEach((variant, i) => {
          const item = mapShopifyProductToGA4Item(product, variant, { index: i });
          registerProduct(variant.id, item);
        });
        // Also register by product ID (used by wishlist buttons' data-product-id)
        if (product.id) {
          const firstItem = mapShopifyProductToGA4Item(product, product.variants[0], { index: 0 });
          registerProduct('product:' + product.id, firstItem);
        }
      }
    } catch (e) {
      // Silently skip malformed JSON
    }
  });
}

/**
 * Populate cache from SearchSpring results.
 * @param {Array} results - SS controller.store.results
 * @param {string} listName
 * @param {string} [listId]
 */
function registerFromSearchSpring(results, listName, listId) {
  if (!results || !results.length) return;
  results.forEach((result, index) => {
    const item = mapSearchSpringResultToGA4Item(result, index, listName, listId);
    // SS results may have multiple variant_ids
    const variantIds = result.attributes?.variant_id || result.variant_id || [];
    const ids = Array.isArray(variantIds) ? variantIds : [variantIds];
    ids.forEach((vid) => {
      if (vid) registerProduct(vid, item);
    });
    // Also register by handle for click matching
    if (item.item_id) {
      registerProduct('handle:' + item.item_id, item);
    }
  });
}

/**
 * Populate cache from quick-add component data-variants attribute.
 * @param {HTMLElement} el - quick-add-component element
 */
function registerFromQuickAdd(el) {
  const title = el.dataset.productTitle || '';
  const handle = el.dataset.productHandle || '';
  const productId = el.dataset.productId || '';
  let variants;
  try {
    variants = JSON.parse(el.dataset.variants || '[]');
  } catch (e) {
    return;
  }
  variants.forEach((v, i) => {
    // Don't overwrite richer data from registerFromShopifyJSON
    if (resolveProduct(v.id)) return;
    registerProduct(v.id, {
      item_id: handle,
      item_name: title,
      item_brand: 'CAMILLA AND MARC',
      item_variant: '', // color not available in quick-add data
      price: +(v.price || 0).toFixed(2),
      quantity: 1,
      affiliation: 'Shopify',
      dimension2: v.size || '',
      dimension3: v.available ? 'In Stock' : 'Out of Stock',
    });
  });
}

// ---------------------------------------------------------------------------
// 7. Event Trackers
// ---------------------------------------------------------------------------

// -- Page View --

function trackPageView() {
  pushEvent('page_view', {
    page_location: window.location.href,
    page_title: document.title,
    page_referrer: document.referrer,
  });
}

// -- Product Page: view_item --

function trackViewItem(product, variant) {
  const item = mapShopifyProductToGA4Item(product, variant);
  pushEcommerceEvent('view_item', {
    currency: getCurrency(),
    value: item.price,
    items: [item],
  });
}

// -- Collection/Search: view_item_list --

function trackViewItemList(items, listName, listId) {
  if (!items || !items.length) return;
  const value = items.reduce((sum, item) => sum + (item.price || 0), 0);
  pushEcommerceEvent('view_item_list', {
    currency: getCurrency(),
    value: +value.toFixed(2),
    item_list_id: listId || '',
    item_list_name: listName || '',
    items: items,
  });
}

// -- Collection/Search: select_item --

function trackSelectItem(item, listName, listId) {
  pushEcommerceEvent('select_item', {
    currency: getCurrency(),
    value: item.price || 0,
    item_list_id: listId || '',
    item_list_name: listName || '',
    items: [item],
  });
}

// -- Cart: add_to_cart --

function trackAddToCart(item, quantity) {
  const cartItem = { ...item, quantity: quantity || 1 };
  pushEcommerceEvent('add_to_cart', {
    currency: getCurrency(),
    value: +(cartItem.price * cartItem.quantity).toFixed(2),
    items: [cartItem],
  });
  // Sync GA ids to cart attrs after the add succeeds (covers cold-start + late-cookie cases).
  syncGAIdsToCart().catch(function () {});
}

// -- Cart: remove_from_cart --

function trackRemoveFromCart(item, quantity) {
  const cartItem = { ...item, quantity: quantity || 1 };
  pushEcommerceEvent('remove_from_cart', {
    currency: getCurrency(),
    value: +(cartItem.price * cartItem.quantity).toFixed(2),
    items: [cartItem],
  });
}

// -- Cart: view_cart --

function trackViewCart(items) {
  const value = items.reduce((sum, item) => sum + (item.price || 0) * (item.quantity || 1), 0);
  pushEcommerceEvent('view_cart', {
    currency: getCurrency(),
    value: +value.toFixed(2),
    items: items,
  });
}

// -- Search --

let _lastSearchTerm = '';
function trackSearch(searchTerm) {
  // Deduplicate — avoid firing multiple times for the same query
  if (searchTerm === _lastSearchTerm) return;
  _lastSearchTerm = searchTerm;
  pushEvent('search', {
    search_term: searchTerm,
  });
}

// -- Generic custom event --

function trackCustomEvent(eventName, params) {
  pushEvent(eventName, params);
}

// ---------------------------------------------------------------------------
// 8. Page View Tracking (initial + SPA view transitions)
// ---------------------------------------------------------------------------

function initPageViewTracking() {
  // Fire on initial page load
  trackPageView();

  // SPA navigation via View Transitions API
  // The theme uses pagereveal for new page content.
  // pagereveal fires on every navigation including the initial page load.
  // On first fire, _initialFired is undefined (falsy) so we skip the body
  // (we already fired page_view above). After that, we set it to true
  // so subsequent SPA navigations will fire page_view.
  window.addEventListener('pagereveal', () => {
    if (initPageViewTracking._initialFired) {
      trackPageView();
      // Re-initialize page-specific tracking after SPA nav
      onPageReady();
    }
    initPageViewTracking._initialFired = true;
  });
}

// ---------------------------------------------------------------------------
// 9. Product Page Tracking (view_item + variant changes)
// ---------------------------------------------------------------------------

let _currentProduct = null;
let _currentVariant = null;

function initProductPageTracking() {
  const ctx = getPageContext();
  if (!ctx.template || !ctx.template.includes('product')) return;

  // Find product JSON on page (try data attribute, then wishlist script tag)
  const productJsonEl =
    document.querySelector('[data-product-json]') ||
    document.querySelector('script[id^="ProductJson-"]');
  if (!productJsonEl) return;

  try {
    _currentProduct = JSON.parse(productJsonEl.textContent);
  } catch (e) {
    return;
  }

  if (!_currentProduct || !_currentProduct.variants) return;

  // Register all variants in cache
  _currentProduct.variants.forEach((variant, i) => {
    const item = mapShopifyProductToGA4Item(_currentProduct, variant, { index: i });
    registerProduct(variant.id, item);
  });

  // Fire initial view_item for selected/first available variant
  _currentVariant =
    _currentProduct.selected_variant ||
    _currentProduct.variants.find((v) => v.available) ||
    _currentProduct.variants[0];

  if (_currentVariant) {
    trackViewItem(_currentProduct, _currentVariant);
  }

  // Listen for variant changes via ThemeEvents
  const debouncedViewItem = debounce((variant) => {
    trackViewItem(_currentProduct, variant);
  }, 300);

  document.addEventListener('variant:selected', (e) => {
    const resource = e.detail?.resource;
    if (!resource || !resource.id) return;
    // Find the full variant from our product data
    const variant = _currentProduct.variants.find((v) => v.id === resource.id);
    if (variant) {
      _currentVariant = variant;
      debouncedViewItem(variant);
    }
  });
}

// ---------------------------------------------------------------------------
// 10. SearchSpring Collection + Search Tracking
// ---------------------------------------------------------------------------

/** Track whether we've already fired view_item_list for this page */
let _ssViewItemListFired = false;

function initSearchSpringTracking() {

  // Only run on collection/search pages with SearchSpring enabled
  const ssContainer = document.getElementById('searchspring-content');
  if (!ssContainer) return;

  // Get collection context from page (set by ss-script.liquid)
  const collectionName = getCollectionName();
  const collectionHandle = getCollectionHandle();

  // Fallback: MutationObserver in case SS controller API isn't accessible
  // Store reference so we can disconnect it once the controller hook succeeds
  const fallbackObserver = setupSSFallbackObserver(ssContainer, collectionName, collectionHandle);

  // Strategy: Poll for window.searchspring controller, then hook into results
  pollForSearchSpring((controller) => {
    // Disconnect fallback observer — controller is the authoritative source
    if (fallbackObserver) fallbackObserver.disconnect();
    hookSearchSpringController(controller, collectionName, collectionHandle);
  });

  // select_item: event delegation for product card clicks
  initSSSelectItem(ssContainer, collectionName, collectionHandle);
}

function getCollectionName() {
  // Try to get from heading, breadcrumb, or page title
  const heading = document.querySelector('.collection-hero__title, .collection__title, h1');
  if (heading) return heading.textContent.trim();
  // Split on en-dash, em-dash, pipe, or hyphen surrounded by spaces
  return document.title.split(/\s*[–—|]\s*|\s+-\s+/)[0].trim();
}

function getCollectionHandle() {
  // Extract from URL path: /collections/{handle}
  const match = window.location.pathname.match(/\/collections\/([^/?#]+)/);
  return match ? match[1] : '';
}

/**
 * Poll for SearchSpring controller availability.
 * @param {Function} callback - Called with the controller instance
 */
function pollForSearchSpring(callback) {
  let attempts = 0;
  const maxAttempts = 100; // 10 seconds at 100ms intervals
  const interval = setInterval(() => {
    attempts++;
    if (window.searchspring?.controller?.search) {
      clearInterval(interval);
      callback(window.searchspring.controller.search);
      return;
    }
    // Also check for collection controller
    if (window.searchspring?.controller?.collection) {
      clearInterval(interval);
      callback(window.searchspring.controller.collection);
      return;
    }
    // Check the generic controllers object
    if (window.searchspring?.controllers) {
      const controllers = Object.values(window.searchspring.controllers);
      for (const ctrl of controllers) {
        if (ctrl.store?.results?.length) {
          clearInterval(interval);
          callback(ctrl);
          return;
        }
      }
    }
    if (attempts >= maxAttempts) {
      clearInterval(interval);
    }
  }, 100);

}

/**
 * Hook into SearchSpring controller to track results.
 */
function hookSearchSpringController(controller, listName, listId) {
  let initialTrackDone = false;

  const tryTrackResults = () => {
    const results = controller.store?.results;
    if (results && results.length > 0 && !_ssViewItemListFired) {
      _ssViewItemListFired = true;
      registerFromSearchSpring(results, listName, listId);
      const items = results
        .map((r, i) => mapSearchSpringResultToGA4Item(r, i, listName, listId))
        .filter((item) => item.item_id); // Omit sold-out placeholders with no product data
      trackViewItemList(items, listName, listId);
    }
  };

  tryTrackResults();

  // Hook into afterStore if available (SS Snap SDK)
  if (typeof controller.on === 'function') {
    controller.on('afterStore', () => {
      if (!initialTrackDone) {
        // First afterStore — allow tracking if tryTrackResults() above missed (results weren't ready)
        initialTrackDone = true;
        tryTrackResults();
      } else {
        // Subsequent afterStore — user changed filters/page, fire new view_item_list
        _ssViewItemListFired = false;
        tryTrackResults();
      }
    });
  }

}

/**
 * Fallback: MutationObserver on #searchspring-content.
 * If the controller API isn't accessible, watch for DOM changes.
 */
function setupSSFallbackObserver(container, listName, listId) {
  let fallbackTimeout;
  const observer = new MutationObserver(() => {
    // Debounce — SS renders progressively
    clearTimeout(fallbackTimeout);
    fallbackTimeout = setTimeout(() => {
      if (_ssViewItemListFired) return;
      // Parse items from rendered DOM
      const items = parseSSItemsFromDOM(container, listName, listId);
      if (items.length > 0) {
        _ssViewItemListFired = true;
        trackViewItemList(items, listName, listId);
      }
    }, 500);
  });

  observer.observe(container, { childList: true, subtree: true });

  // Disconnect after 30 seconds to avoid performance impact
  setTimeout(() => observer.disconnect(), 30000);

  return observer;
}

/**
 * Parse product data from SearchSpring rendered DOM as fallback.
 */
function parseSSItemsFromDOM(container, listName, listId) {
  const items = [];
  // SS renders product cards with various class patterns
  const cards = container.querySelectorAll('[class*="ss-result"], .ss__result, a[href*="/products/"]');
  cards.forEach((card, index) => {
    const link = card.tagName === 'A' ? card : card.querySelector('a[href*="/products/"]');
    if (!link) return;

    const href = link.getAttribute('href') || '';
    const handle = href.split('/products/').pop()?.split('?')[0]?.split('#')[0] || '';
    if (!handle) return;

    const nameEl = card.querySelector('[class*="title"], [class*="name"], h2, h3');
    const priceEl = card.querySelector('[class*="price"]');

    const name = nameEl ? nameEl.textContent.trim() : '';
    const priceText = priceEl ? priceEl.textContent.replace(/[^0-9.]/g, '') : '0';
    const price = parseFloat(priceText) || 0;

    const item = {
      item_id: handle,
      item_name: name,
      item_brand: 'CAMILLA AND MARC',
      price: +price.toFixed(2),
      quantity: 1,
      index: index,
      affiliation: 'Shopify',
    };
    if (listName) item.item_list_name = listName;
    if (listId) item.item_list_id = listId;

    items.push(item);
    // Register by handle for select_item matching
    registerProduct('handle:' + handle, item);
  });
  return items;
}

/**
 * Track select_item when user clicks a product card on SS pages.
 */
function initSSSelectItem(container, listName, listId) {
  container.addEventListener('click', (e) => {
    const link = e.target.closest('a[href*="/products/"]');
    if (!link) return;

    const href = link.getAttribute('href') || '';
    const handle = href.split('/products/').pop()?.split('?')[0]?.split('#')[0] || '';
    if (!handle) return;

    const item = resolveProduct('handle:' + handle);
    if (item) {
      trackSelectItem(item, listName, listId);
    } else {
      // Minimal tracking if not in cache
      trackSelectItem(
        {
          item_id: handle,
          item_name: link.querySelector('[class*="title"], [class*="name"], h2, h3')?.textContent?.trim() || '',
          item_brand: 'CAMILLA AND MARC',
          affiliation: 'Shopify',
        },
        listName,
        listId
      );
    }
  });
}

// Also track select_item on native (non-SS) collection grids
function initNativeSelectItem() {
  const grid = document.querySelector('.main-collection-grid');
  if (!grid) return;
  // Only if SS is NOT enabled (SS has its own handler above)
  if (document.getElementById('searchspring-content')) return;

  const listName = getCollectionName();
  const listId = getCollectionHandle();

  grid.addEventListener('click', (e) => {
    const link = e.target.closest('a[href*="/products/"]');
    if (!link) return;

    const href = link.getAttribute('href') || '';
    const handle = href.split('/products/').pop()?.split('?')[0]?.split('#')[0] || '';
    if (!handle) return;

    const item = resolveProduct('handle:' + handle) || {
      item_id: handle,
      item_brand: 'CAMILLA AND MARC',
      affiliation: 'Shopify',
    };

    trackSelectItem(item, listName, listId);
  });
}

// ---------------------------------------------------------------------------
// 11. Cart Event Tracking
// ---------------------------------------------------------------------------

function initCartTracking() {
  // add_to_cart — Listen on document only.
  // All CartAddEvents bubble to document. global-add-to-cart.js also dispatches
  // to window, but we intentionally skip window to avoid double-firing.
  let lastCartAddKey = '';

  document.addEventListener('cart:update', (e) => {
    const detail = e.detail || {};
    if (detail.data?.didError) return; // Skip error events
    // Skip events from cart-items-component (those are quantity changes, not adds)
    if (detail.data?.source === 'cart-items-component') return;

    const variantId = detail.sourceId || detail.data?.variantId;
    if (!variantId) return;

    // Deduplicate within 500ms window (same variant)
    const key = variantId + ':' + Math.floor(Date.now() / 500);
    if (key === lastCartAddKey) return;
    lastCartAddKey = key;

    const quantity = detail.data?.itemCount || 1;
    const item = resolveProduct(variantId);

    if (item) {
      trackAddToCart(item, quantity);
    } else {
      // Fallback: fetch product data from cart API
      fetchCartAndTrackAdd(variantId, quantity);
    }
  });

  // view_cart — When cart drawer opens
  initViewCartTracking();

  // remove_from_cart — Hook into cart item quantity changes
  initRemoveFromCartTracking();
}

/**
 * Fallback: fetch cart JSON to get product data for add_to_cart.
 */
async function fetchCartAndTrackAdd(variantId, quantity) {
  try {
    const response = await fetch('/cart.js');
    const cart = await response.json();
    const lineItem = cart.items.find((item) => String(item.variant_id) === String(variantId));
    if (lineItem) {
      const item = {
        item_id: lineItem.handle || '',
        item_name: lineItem.product_title || '',
        item_brand: lineItem.vendor || 'CAMILLA AND MARC',
        item_category: lineItem.product_type || '',
        item_variant: lineItem.variant_title || '',
        price: +(lineItem.final_price / 100).toFixed(2),
        quantity: quantity,
        affiliation: 'Shopify',
      };
      trackAddToCart(item, quantity);
    }
  } catch (e) {
    // Silent fail — don't break UX for analytics
  }
}

/**
 * Track view_cart when the cart drawer opens.
 */
function initViewCartTracking() {
  // Cart drawer extends DialogComponent which dispatches 'dialog:open' on showDialog()
  const cartDrawer = document.querySelector('cart-drawer-component');
  if (cartDrawer) {
    cartDrawer.addEventListener('dialog:open', () => {
      fetchCartAndTrackView();
    });
  }

  // Also track on cart page template
  const ctx = getPageContext();
  if (ctx.template && ctx.template.includes('cart')) {
    fetchCartAndTrackView();
  }
}

async function fetchCartAndTrackView() {
  try {
    const response = await fetch('/cart.js');
    const cart = await response.json();
    const items = cart.items.map((lineItem) => ({
      item_id: lineItem.handle || '',
      item_name: lineItem.product_title || '',
      item_brand: lineItem.vendor || 'CAMILLA AND MARC',
      item_category: lineItem.product_type || '',
      item_variant: lineItem.variant_title || '',
      price: +(lineItem.final_price / 100).toFixed(2),
      quantity: lineItem.quantity || 1,
      affiliation: 'Shopify',
    }));
    trackViewCart(items);
  } catch (e) {
    // Silent fail
  }
}

/**
 * Track remove_from_cart by intercepting cart item quantity decreases.
 * We store cart state before changes and compare after.
 */
function initRemoveFromCartTracking() {
  let previousCartState = null;

  // Capture cart state periodically and on interactions
  async function captureCartState() {
    try {
      const response = await fetch('/cart.js');
      previousCartState = await response.json();
    } catch (e) {
      // Silent fail
    }
  }

  // Capture initial state
  captureCartState();

  // Listen for cart updates from cart-items-component (quantity changes / removals)
  document.addEventListener('cart:update', async (e) => {
    const source = e.detail?.data?.source;
    if (source !== 'cart-items-component') return;
    // This is a quantity change or removal from the cart drawer/page

    // Small delay to allow the cart API to update
    await new Promise((r) => setTimeout(r, 100));

    try {
      const response = await fetch('/cart.js');
      const newCart = await response.json();

      if (previousCartState) {
        // Compare: find items that decreased in quantity or were removed
        const prevItems = new Map();
        previousCartState.items.forEach((item) => {
          prevItems.set(String(item.variant_id), item);
        });

        const newItems = new Map();
        newCart.items.forEach((item) => {
          newItems.set(String(item.variant_id), item);
        });

        prevItems.forEach((prevItem, variantId) => {
          const newItem = newItems.get(variantId);
          const prevQty = prevItem.quantity;
          const newQty = newItem ? newItem.quantity : 0;

          if (newQty < prevQty) {
            const removedQty = prevQty - newQty;
            const item = {
              item_id: prevItem.handle || '',
              item_name: prevItem.product_title || '',
              item_brand: prevItem.vendor || 'CAMILLA AND MARC',
              item_category: prevItem.product_type || '',
              item_variant: prevItem.variant_title || '',
              price: +(prevItem.final_price / 100).toFixed(2),
              quantity: removedQty,
              affiliation: 'Shopify',
            };
            trackRemoveFromCart(item, removedQty);
          }
        });
      }

      previousCartState = newCart;
    } catch (e) {
      // Silent fail
    }
  });

  // Re-capture on every add (non-removal) cart update
  window.addEventListener('cart:update', (e) => {
    const source = e.detail?.data?.source;
    if (source !== 'cart-items-component') {
      captureCartState();
    }
  });
}

// ---------------------------------------------------------------------------
// 12. Custom Events (Vera's audit list)
// ---------------------------------------------------------------------------

// -- search --
function initSearchTracking() {
  // URL-based search detection is handled in onPageReady() (works for both
  // initial page load and SPA navigations to search pages)

  // 1. Hook into predictive search render event (Shopify native autocomplete)
  document.addEventListener('predictive-search:render', () => {
    const input = document.querySelector('predictive-search-component input[type="search"], .predictive-search__input, input[name="q"]');
    if (input && input.value.trim()) {
      trackSearch(input.value.trim());
    }
  });

  // 3. Track search form submissions
  document.addEventListener('submit', (e) => {
    const form = e.target;
    if (form.action && form.action.includes('/search')) {
      const input = form.querySelector('input[name="q"]');
      if (input && input.value.trim()) {
        trackSearch(input.value.trim());
      }
    }
  });
}

// -- newsletter_signups --
function initNewsletterTracking() {
  document.addEventListener('submit', (e) => {
    const form = e.target;
    // Newsletter forms typically have a specific action or class
    const isNewsletter =
      form.classList.contains('newsletter-form') ||
      form.closest('[class*="newsletter"]') ||
      form.closest('[class*="footer"]')?.querySelector('input[type="email"]') === form.querySelector('input[type="email"]') ||
      (form.action && form.action.includes('newsletter'));

    if (!isNewsletter) return;
    if (!form.querySelector('input[type="email"]')?.value) return;

    const isPopup = !!form.closest('[class*="popup"], [class*="modal"]');
    trackCustomEvent('newsletter_signups', {
      event_action: isPopup ? 'Popup' : 'Footer',
    });
  });
}

// -- Account events --
function initAccountTracking() {
  const ctx = getPageContext();
  const path = window.location.pathname;

  // new_account_creation — detect registration success
  if (path.includes('/account') && !path.includes('/login') && !path.includes('/register')) {
    // If we're on the account page (not login/register), check if this is a new registration
    // by looking for a flash message or referrer
    if (document.referrer.includes('/account/register')) {
      trackCustomEvent('new_account_creation', {});
    }
  }

  // account_login_successful — detect login
  if (path.includes('/account') && !path.includes('/login') && !path.includes('/register')) {
    if (document.referrer.includes('/account/login')) {
      trackCustomEvent('account_login_successful', {});
    }
  }

  // account_login_error — detect failed login on login page
  if (path.includes('/account/login')) {
    const errorEl = document.querySelector('.errors, [class*="error"], .form__message--error');
    if (errorEl && errorEl.textContent.trim()) {
      trackCustomEvent('account_login_error', {
        error_message: errorEl.textContent.trim().substring(0, 100),
      });
    }
  }

  // account_forgot_password
  const resetForm = document.querySelector('#recover, form[action*="recover"]');
  if (resetForm) {
    resetForm.addEventListener('submit', () => {
      trackCustomEvent('account_forgot_password', {});
    });
  }
}

// -- header_nav_clicks --
function initHeaderNavTracking() {
  const header = document.querySelector('#header-group, header');
  if (!header) return;

  header.addEventListener('click', (e) => {
    const link = e.target.closest('a');
    if (!link) return;
    // Only track primary nav links, not utility links
    if (link.closest('nav, [class*="menu"], [class*="nav"]')) {
      trackCustomEvent('header_nav_clicks', {
        link_text: link.textContent.trim().substring(0, 100),
        link_url: link.getAttribute('href') || '',
      });
    }
  });
}

// -- color_size_filter --
function initFilterTracking() {
  document.addEventListener('filter:update', (e) => {
    const queryParams = e.detail?.queryParams;
    if (!queryParams) return;

    const filters = {};
    let color = '';
    let size = '';
    let count = 0;

    for (const [key, value] of queryParams.entries()) {
      if (key.startsWith('filter.')) {
        filters[key] = value;
        if (key.toLowerCase().includes('color') || key.toLowerCase().includes('colour')) {
          color = value;
        }
        if (key.toLowerCase().includes('size')) {
          size = value;
        }
      }
    }

    // Try to get result count from DOM
    const countEl = document.querySelector('[class*="result-count"], [class*="product-count"]');
    if (countEl) {
      const num = parseInt(countEl.textContent.replace(/\D/g, ''), 10);
      if (!isNaN(num)) count = num;
    }

    trackCustomEvent('color_size_filter', {
      collection_category: getCollectionName(),
      selected_color: color,
      selected_size: size,
      search_result_count: count,
      all_filters: JSON.stringify(filters),
    });
  });
}

// -- add_to_wishlist --
function initWishlistTracking() {
  // Wishlist buttons use class="btn-wishlist" with data-product-id and data-product-title
  document.addEventListener('click', (e) => {
    const btn = e.target.closest(
      '.btn-wishlist, [class*="wishlist"]'
    );
    if (!btn) return;

    let productName = '';
    let productHandle = '';
    let productPrice = 0;

    // The btn-wishlist div itself has data-product-id and data-product-title
    if (btn.dataset.productTitle) {
      productName = btn.dataset.productTitle;
    }

    // Try to resolve product from cache using product ID (stored as product:{id})
    const productId = btn.dataset.productId;
    if (productId) {
      const cached = resolveProduct('product:' + productId);
      if (cached) {
        productName = cached.item_name || productName;
        productHandle = cached.item_id || '';
        productPrice = cached.price || 0;
      }
    }

    // Fallback: On PDP, read from page
    if (!productName) {
      productName = document.querySelector('.product__title, h1')?.textContent?.trim() || '';
    }
    if (!productHandle) {
      // Try to extract from URL on PDP
      const match = window.location.pathname.match(/\/products\/([^/?#]+)/);
      productHandle = match ? match[1] : '';
    }
    if (!productPrice) {
      const priceEl = document.querySelector('product-price-component .price, .product__price .price');
      productPrice = priceEl ? parseFloat(priceEl.textContent.replace(/[^0-9.]/g, '')) || 0 : 0;
    }

    pushEcommerceEvent('add_to_wishlist', {
      currency: getCurrency(),
      value: productPrice,
      items: [{
        item_id: productHandle,
        item_name: productName,
        item_brand: 'CAMILLA AND MARC',
        price: productPrice,
        quantity: 1,
        affiliation: 'Shopify',
      }],
    });
  });
}

// -- homepg_product_click --
function initHomepageProductClickTracking() {
  const ctx = getPageContext();
  if (!ctx.template || ctx.template !== 'index') return;

  document.addEventListener('click', (e) => {
    const link = e.target.closest('a[href*="/products/"]');
    if (!link) return;

    const href = link.getAttribute('href') || '';
    const handle = href.split('/products/').pop()?.split('?')[0]?.split('#')[0] || '';

    // Determine which section/block this product is in
    const section = link.closest('[class*="section"]');
    const blockName = section ? section.className.split(' ')[0] : '';

    const nameEl = link.querySelector('[class*="title"], [class*="name"], h2, h3');
    const priceEl = link.querySelector('[class*="price"]');

    trackCustomEvent('homepg_product_click', {
      product_name: nameEl ? nameEl.textContent.trim() : '',
      product_id: handle,
      product_price: priceEl ? priceEl.textContent.replace(/[^0-9.]/g, '') : '',
      homepg_product_blocks: blockName,
    });
  });
}

// ---------------------------------------------------------------------------
// 13. Quick-Add Cache Population
// ---------------------------------------------------------------------------

function initQuickAddCachePopulation() {
  // Register variants from all quick-add components currently on page
  document.querySelectorAll('quick-add-component').forEach(registerFromQuickAdd);

  // Also register from newly added quick-add components (infinite scroll, etc.)
  const observer = new MutationObserver((mutations) => {
    for (const m of mutations) {
      m.addedNodes.forEach((node) => {
        if (node.nodeType !== 1) return;
        if (node.tagName === 'QUICK-ADD-COMPONENT') {
          registerFromQuickAdd(node);
        }
        node.querySelectorAll?.('quick-add-component')?.forEach(registerFromQuickAdd);
      });
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });
}

// ---------------------------------------------------------------------------
// 14. Utility
// ---------------------------------------------------------------------------

function debounce(fn, ms) {
  let timer;
  return function (...args) {
    clearTimeout(timer);
    timer = setTimeout(() => fn.apply(this, args), ms);
  };
}

// ---------------------------------------------------------------------------
// 15. Initialization
// ---------------------------------------------------------------------------

/**
 * Run page-specific tracking setup.
 * Called on initial load and after SPA navigations.
 */
function onPageReady() {
  // Reset page-specific state
  _ssViewItemListFired = false;
  _currentProduct = null;
  _currentVariant = null;

  // Populate cache from page data
  registerFromShopifyJSON();

  // Page-specific tracking
  initProductPageTracking();
  initSearchSpringTracking();
  initNativeSelectItem();
  initQuickAddCachePopulation();

  // Custom events that need page context
  initAccountTracking();
  initHomepageProductClickTracking();

  // Detect search term from URL (for SPA navigations to search pages)
  const urlSearchTerm = new URLSearchParams(window.location.search).get('q');
  if (urlSearchTerm && urlSearchTerm.trim()) {
    trackSearch(urlSearchTerm.trim());
  }
}

// ---------------------------------------------------------------------------
// GA4 client_id capture -> Shopify cart attributes
// ---------------------------------------------------------------------------
// On checkout, the order's note_attributes will carry these so middleware
// can correlate the server-side purchase_classification event with the
// pixel-emitted purchase event via the same client_id.

/**
 * Read the GA4 client_id from the _ga cookie.
 * _ga cookie format: GA1.<version>.<part1>.<part2>
 * Returns the "<part1>.<part2>" string (or null if absent).
 */
function getGAClientId() {
  var m = document.cookie.match(/(?:^|;\s*)_ga=GA\d\.\d\.([^;]+)/);
  return m ? m[1] : null;
}

/**
 * Read the GA4 session_id from the _ga_<MEASUREMENT_ID_SUFFIX> cookie.
 * Cookie format varies by GA4 version:
 *   GS1.1.<sessionId>.<...>        (older)
 *   GS2.1.s<sessionId>$o<n>$g<n>   (current as of 2026)
 * The s? makes the prefix optional so both forms work.
 */
function getGASessionId() {
  if (typeof MEASUREMENT_ID === 'undefined' || !MEASUREMENT_ID) return null;
  var suffix = MEASUREMENT_ID.replace(/^G-/, '');
  var re = new RegExp('(?:^|;\\s*)_ga_' + suffix + '=GS\\d\\.\\d\\.s?(\\d+)');
  var m = document.cookie.match(re);
  return m ? m[1] : null;
}

/**
 * Write the current GA4 ids into Shopify cart attributes so the orders/create
 * webhook carries them via note_attributes. Idempotent. Best-effort: silent
 * skip on consent-gated / AdBlock / ITP / network failure.
 */
async function syncGAIdsToCart() {
  var ga_client_id = getGAClientId();
  if (!ga_client_id) return;
  var ga_session_id = getGASessionId() || '';
  try {
    await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: { ga_client_id: ga_client_id, ga_session_id: ga_session_id } })
    });
  } catch (_) {
    // best-effort
  }
}

/**
 * Main init - called once on module load.
 */
function init() {
  initDataLayer();

  // Page view tracking (handles both initial + SPA navigations)
  initPageViewTracking();

  // Cart tracking (global, persists across SPA navigations)
  initCartTracking();

  // Custom events (global listeners)
  initSearchTracking();
  initNewsletterTracking();
  initHeaderNavTracking();
  initFilterTracking();
  initWishlistTracking();

  // Page-specific setup
  onPageReady();

  // Capture GA4 client_id + session_id into Shopify cart attributes (best-effort, idempotent).
  syncGAIdsToCart();

  // Last-chance ga_client_id sync if user clicks any checkout CTA without going through cart page first.
  document.addEventListener('click', function (e) {
    if (e.target && e.target.closest && e.target.closest('[name="checkout"], a[href="/checkout"], button[name="checkout"]')) {
      syncGAIdsToCart();
    }
  }, true);
}

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

window.cmAnalytics = window.cmAnalytics || {};
window.cmAnalytics.registerFromShopifyJSON = registerFromShopifyJSON;
window.cmAnalytics.registerFromQuickAdd = registerFromQuickAdd;

})(); // End IIFE

/*
 * Native viewport-based PLP impression tracking (added 2026-06-23 for Phase 1
 * SearchSpring → Depict migration). Replaces the SS-controller polling which is
 * dormant when settings.ss_enable=false. Fires view_item_list when a product
 * card enters viewport (threshold 0.5) and select_item on card click.
 * Uses pushEcommerceEvent() to match the project's ecommerce event shape
 * (clears previous ecommerce object, includes currency/value/item_list fields).
 * Uses dedup WeakSet to avoid duplicate fires on scroll-back-in-view.
 */
(function () {
  if (window.__cmNativePlpTrackingInstalled) return;
  window.__cmNativePlpTrackingInstalled = true;

  const seen = new WeakSet();

  function extractProductData(card, index) {
    // data-product-id and data-product-handle are on <product-card> directly
    const id = card.dataset.productId || card.getAttribute('data-product-id') || null;
    const handle = card.dataset.productHandle || card.getAttribute('data-product-handle') || null;

    // Title: prefer the JSON script block, fall back to visually-hidden span in product-card__link
    let name = '';
    const jsonScript = id ? card.querySelector('script[id^="ProductData-"]') : null;
    if (jsonScript) {
      try {
        const data = JSON.parse(jsonScript.textContent);
        name = data.title || '';
      } catch (_) { /* ignore */ }
    }
    if (!name) {
      name = card.querySelector('.product-card__link .visually-hidden, .product-card__name, .product-card__title, h3, h4')?.textContent?.trim() || '';
    }

    // Price: attempt to read from a price element if present
    const priceEl = card.querySelector('[class*="price"]');
    const priceText = priceEl ? priceEl.textContent.replace(/[^0-9.]/g, '') : '0';
    const price = parseFloat(priceText) || 0;

    return { id, name, handle, price, index: index != null ? index : 0 };
  }

  function buildItem(p, listName, listId) {
    return {
      item_id: p.id || p.handle || '',
      item_name: p.name,
      item_brand: 'CAMILLA AND MARC',
      price: +p.price.toFixed(2),
      quantity: 1,
      index: p.index,
      affiliation: 'Shopify',
      item_list_name: listName || '',
      item_list_id: listId || '',
    };
  }

  function getListName() {
    const heading = document.querySelector('.collection-hero__title, .collection__title, h1');
    if (heading) return heading.textContent.trim();
    return document.title.split(/\s*[–—|]\s*|\s+-\s+/)[0].trim();
  }

  function getListId() {
    const match = window.location.pathname.match(/\/collections\/([^/?#]+)/);
    return match ? match[1] : '';
  }

  function dlPush(eventName, ecommerceData) {
    if (!window.dataLayer) window.dataLayer = [];
    // Clear previous ecommerce data to prevent bleed between events (matches project convention)
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({ event: eventName, ecommerce: ecommerceData });
  }

  function getCurrencyLocal() {
    if (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) {
      return window.Shopify.currency.active;
    }
    const meta = document.querySelector('meta[property="og:price:currency"]');
    if (meta) return meta.content;
    return 'USD';
  }

  function fireViewItemList(card, index) {
    const p = extractProductData(card, index);
    if (!p.id && !p.handle) return;
    const listName = getListName();
    const listId = getListId();
    const item = buildItem(p, listName, listId);
    dlPush('view_item_list', {
      currency: getCurrencyLocal(),
      value: item.price,
      item_list_id: listId,
      item_list_name: listName,
      items: [item],
    });
  }

  function fireSelectItem(card) {
    const p = extractProductData(card, null);
    if (!p.id && !p.handle) return;
    const listName = getListName();
    const listId = getListId();
    const item = buildItem(p, listName, listId);
    dlPush('select_item', {
      currency: getCurrencyLocal(),
      value: item.price,
      item_list_id: listId,
      item_list_name: listName,
      items: [item],
    });
  }

  const cardObserver = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting || seen.has(entry.target)) continue;
      seen.add(entry.target);
      try {
        // Determine index among all currently observed cards
        const allCards = Array.from(document.querySelectorAll('.product-card, .product-grid__card'));
        const index = allCards.indexOf(entry.target);
        fireViewItemList(entry.target, index >= 0 ? index : 0);
      } catch (e) { /* never throw out of analytics */ }
    }
  }, { threshold: 0.5 });

  function attachToCards(root) {
    root.querySelectorAll('.product-card, .product-grid__card').forEach((card) => {
      if (!seen.has(card)) cardObserver.observe(card);
    });
  }

  // Initial attach
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => attachToCards(document));
  } else {
    attachToCards(document);
  }

  // Re-attach for dynamically-loaded cards (infinite scroll, AJAX filter)
  const treeObserver = new MutationObserver((mutations) => {
    for (const m of mutations) {
      for (const node of m.addedNodes) {
        if (node.nodeType !== 1) continue;
        const el = node;
        if (el.matches && el.matches('.product-card, .product-grid__card')) {
          if (!seen.has(el)) cardObserver.observe(el);
        } else if (el.querySelectorAll) {
          attachToCards(el);
        }
      }
    }
  });
  treeObserver.observe(document.body || document.documentElement, { childList: true, subtree: true });

  // Delegated click → select_item
  document.addEventListener('click', (e) => {
    const link = e.target.closest('.product-card a, .product-card__link');
    if (!link) return;
    const card = link.closest('.product-card, .product-grid__card');
    if (!card) return;
    try { fireSelectItem(card); } catch (e) { /* never throw out of analytics */ }
  }, true);
})();
